import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useSubscription } from '@apollo/client';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';
import { Trash2, Edit2, Check, X as XIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { MentionTextarea, MentionMember } from '../ui/MentionTextarea';
import { TypingIndicator } from './TypingIndicator';
import {
  GET_TASK_COMMENTS,
  CREATE_TASK_COMMENT_MUTATION,
  UPDATE_TASK_COMMENT_MUTATION,
  DELETE_TASK_COMMENT_MUTATION,
  SIGNAL_TASK_COMMENT_TYPING_MUTATION,
  PROJECT_BOARD_CHANGED_SUBSCRIPTION,
} from '../../graphql/queries';

interface TaskCommentsProps {
  taskId: string;
  projectId: string;
  currentUserId: string;
  members: MentionMember[];
  canManageProject?: boolean;
}

interface Comment {
  id: string;
  taskId: string;
  authorId: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  author?: { id: string; firstName: string; lastName: string } | null;
  mentions?: Array<{
    id: string;
    mentionedEmployeeId: string;
    readAt?: string | null;
  }> | null;
}

const TYPING_TTL_MS = 5000;
const TYPING_THROTTLE_MS = 3000;

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'przed chwilą';
  if (min < 60) return `${min} min temu`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} godz. temu`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days} dni temu`;
  return d.toLocaleString('pl-PL', {
    timeZone: 'Europe/Warsaw',
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function renderContentWithMentions(content: string): string {
  // Replace `@[Name](id)` with a styled span readable by react-markdown.
  return content.replace(
    /@\[([^\]]+)\]\(([\w-]+)\)/g,
    (_, name) => `**@${name}**`,
  );
}

function initials(firstName?: string, lastName?: string): string {
  return `${(firstName?.[0] || '').toUpperCase()}${(lastName?.[0] || '').toUpperCase()}` || '?';
}

export const TaskComments: React.FC<TaskCommentsProps> = ({
  taskId,
  projectId,
  currentUserId,
  members,
  canManageProject,
}) => {
  const { data, refetch, loading } = useQuery<{ taskComments: Comment[] }>(
    GET_TASK_COMMENTS,
    {
      variables: { taskId },
      fetchPolicy: 'cache-and-network',
    },
  );

  const [createComment, { loading: creating }] = useMutation(
    CREATE_TASK_COMMENT_MUTATION,
  );
  const [updateComment] = useMutation(UPDATE_TASK_COMMENT_MUTATION);
  const [deleteComment] = useMutation(DELETE_TASK_COMMENT_MUTATION);
  const [signalTyping] = useMutation(SIGNAL_TASK_COMMENT_TYPING_MUTATION);

  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingDraft, setEditingDraft] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<Comment | null>(null);

  const lastTypingSentRef = useRef(0);

  const [typingPeople, setTypingPeople] = useState<
    Map<string, { firstName: string; lastName: string; lastSeen: number }>
  >(new Map());

  // Live updates for this project — listen for comment-related events.
  useSubscription(PROJECT_BOARD_CHANGED_SUBSCRIPTION, {
    variables: { projectId },
    onData: ({ data: subData }) => {
      const evt = subData.data?.projectBoardChanged;
      if (!evt) return;
      const payload = evt.payload ? JSON.parse(evt.payload) : null;
      if (
        evt.type === 'TASK_COMMENT_ADDED' ||
        evt.type === 'TASK_COMMENT_UPDATED' ||
        evt.type === 'TASK_COMMENT_DELETED'
      ) {
        if (payload?.taskId === taskId) {
          refetch().catch(() => {});
        }
      } else if (evt.type === 'TASK_COMMENT_TYPING') {
        if (
          payload?.taskId === taskId &&
          payload?.employeeId &&
          payload.employeeId !== currentUserId
        ) {
          setTypingPeople((prev) => {
            const next = new Map(prev);
            if (payload.isTyping) {
              next.set(payload.employeeId, {
                firstName: payload.firstName,
                lastName: payload.lastName,
                lastSeen: Date.now(),
              });
            } else {
              next.delete(payload.employeeId);
            }
            return next;
          });
        }
      }
    },
    onError: () => {
      /* expected during reconnects */
    },
  });

  // Auto-cleanup stale "typing" entries every second.
  useEffect(() => {
    const t = setInterval(() => {
      setTypingPeople((prev) => {
        let changed = false;
        const next = new Map(prev);
        prev.forEach((info, id) => {
          if (Date.now() - info.lastSeen > TYPING_TTL_MS) {
            next.delete(id);
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const comments = useMemo(
    () => [...(data?.taskComments || [])],
    [data],
  );

  const handleTyping = () => {
    const now = Date.now();
    if (now - lastTypingSentRef.current < TYPING_THROTTLE_MS) return;
    lastTypingSentRef.current = now;
    signalTyping({ variables: { taskId, isTyping: true } }).catch(() => {});
  };

  const handleStopTyping = () => {
    lastTypingSentRef.current = 0;
    signalTyping({ variables: { taskId, isTyping: false } }).catch(() => {});
  };

  const handleSubmit = async () => {
    const content = draft.trim();
    if (!content) return;
    try {
      await createComment({
        variables: { input: { taskId, content } },
      });
      setDraft('');
      handleStopTyping();
      refetch().catch(() => {});
    } catch (err: any) {
      toast.error(err?.message || 'Nie udało się dodać komentarza');
    }
  };

  const startEdit = (c: Comment) => {
    setEditingId(c.id);
    setEditingDraft(c.content);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingDraft('');
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;
    const content = editingDraft.trim();
    if (!content) return;
    try {
      await updateComment({
        variables: { id: editingId, input: { content } },
      });
      cancelEdit();
      refetch().catch(() => {});
    } catch (err: any) {
      toast.error(err?.message || 'Nie udało się zaktualizować komentarza');
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteComment({ variables: { id: confirmDelete.id } });
      setConfirmDelete(null);
      refetch().catch(() => {});
    } catch (err: any) {
      toast.error(err?.message || 'Nie udało się usunąć komentarza');
    }
  };

  const typingPeopleArr = useMemo(() => {
    const arr: Array<{
      employeeId: string;
      firstName: string;
      lastName: string;
    }> = [];
    typingPeople.forEach((info, employeeId) => {
      arr.push({
        employeeId,
        firstName: info.firstName,
        lastName: info.lastName,
      });
    });
    return arr;
  }, [typingPeople]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">
          Komentarze {comments.length > 0 && (
            <span className="text-muted-foreground font-normal">
              ({comments.length})
            </span>
          )}
        </h4>
      </div>

      {loading && comments.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">Wczytywanie…</p>
      ) : comments.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">
          Brak komentarzy. Zacznij rozmowę poniżej.
        </p>
      ) : (
        <div className="space-y-3">
          {comments.map((c) => {
            const isMine = c.authorId === currentUserId;
            const canDelete = isMine || !!canManageProject;
            const edited =
              new Date(c.updatedAt).getTime() -
                new Date(c.createdAt).getTime() >
              1000;
            return (
              <div
                key={c.id}
                className="flex gap-3 p-3 bg-muted/40 dark:bg-muted/20 rounded-md border border-border"
              >
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/20 text-primary text-xs font-semibold flex items-center justify-center">
                  {initials(c.author?.firstName, c.author?.lastName)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="flex items-baseline gap-2 min-w-0">
                      <span className="text-sm font-medium truncate">
                        {c.author?.lastName} {c.author?.firstName}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatRelative(c.createdAt)}
                        {edited && (
                          <span className="ml-1 italic">(edytowano)</span>
                        )}
                      </span>
                    </div>
                    {editingId !== c.id && (isMine || canDelete) && (
                      <div className="flex gap-1 flex-shrink-0">
                        {isMine && (
                          <button
                            type="button"
                            onClick={() => startEdit(c)}
                            className="text-muted-foreground hover:text-foreground p-1"
                            aria-label="Edytuj"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            type="button"
                            onClick={() => setConfirmDelete(c)}
                            className="text-muted-foreground hover:text-destructive p-1"
                            aria-label="Usuń"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {editingId === c.id ? (
                    <div className="mt-2 space-y-2">
                      <MentionTextarea
                        value={editingDraft}
                        onChange={setEditingDraft}
                        members={members}
                        rows={3}
                        onSubmit={handleSaveEdit}
                      />
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          onClick={handleSaveEdit}
                          disabled={!editingDraft.trim()}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Zapisz
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={cancelEdit}
                        >
                          <XIcon className="w-4 h-4 mr-1" />
                          Anuluj
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm prose prose-sm dark:prose-invert max-w-none mt-1 break-words">
                      <ReactMarkdown>
                        {renderContentWithMentions(c.content)}
                      </ReactMarkdown>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <TypingIndicator people={typingPeopleArr} />

      <div className="space-y-2 pt-2 border-t border-border">
        <MentionTextarea
          value={draft}
          onChange={setDraft}
          members={members}
          placeholder="Dodaj komentarz… Wpisz @ aby wspomnieć osobę. Ctrl+Enter aby wysłać."
          rows={3}
          onSubmit={handleSubmit}
          onTyping={handleTyping}
          onStopTyping={handleStopTyping}
        />
        <div className="flex justify-end">
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={creating || !draft.trim()}
            size="sm"
          >
            {creating ? 'Wysyłanie…' : 'Dodaj komentarz'}
          </Button>
        </div>
      </div>

      <Dialog
        open={confirmDelete !== null}
        onOpenChange={(v) => {
          if (!v) setConfirmDelete(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Usuń komentarz</DialogTitle>
            <DialogDescription>
              Czy na pewno chcesz usunąć ten komentarz? Tej operacji nie można
              cofnąć.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              Anuluj
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete}>
              Usuń
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
